import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Square,
  RectangleHorizontal,
  RectangleVertical,
  Type,
  Image,
  Layout,
  Grid3X3,
  Menu,
  Search,
  User,
  ShoppingCart,
  Plus,
  Minus,
  Download,
  Save,
  Smartphone,
  Tablet,
  Monitor,
  Trash2,
  Copy,
} from 'lucide-react';
import {
  RequirementsService,
  type Wireframe,
} from '../../services/requirementsService';
import { openaiService } from '../../services/openaiService';
import { logger } from '../../services/loggingService';

interface WireframeEditorProps {
  projectId: string;
  userId: string;
  wireframeId?: string;
  onSave?: (wireframe: Wireframe) => void;
  onClose?: () => void;
}

interface WireframeComponent {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  properties: {
    text?: string;
    color?: string;
    backgroundColor?: string;
    fontSize?: number;
    fontWeight?: string;
    borderRadius?: number;
    borderWidth?: number;
    borderColor?: string;
    padding?: number;
    margin?: number;
    zIndex?: number;
    locked?: boolean;
    [key: string]: any;
  };
  children?: WireframeComponent[];
}

interface ComponentTemplate {
  id: string;
  name: string;
  icon: React.ReactNode;
  category: string;
  defaultComponent: Omit<WireframeComponent, 'id'>;
}

const COMPONENT_TEMPLATES: ComponentTemplate[] = [
  // Layout Components
  {
    id: 'container',
    name: 'Container',
    icon: <Square className='w-4 h-4' />,
    category: 'Layout',
    defaultComponent: {
      type: 'container',
      x: 0,
      y: 0,
      width: 200,
      height: 150,
      properties: {
        backgroundColor: '#f3f4f6',
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 4,
        padding: 16,
      },
    },
  },
  {
    id: 'header',
    name: 'Header',
    icon: <RectangleHorizontal className='w-4 h-4' />,
    category: 'Layout',
    defaultComponent: {
      type: 'header',
      x: 0,
      y: 0,
      width: 800,
      height: 60,
      properties: {
        backgroundColor: '#1f2937',
        color: '#ffffff',
        padding: 16,
        text: 'Header',
      },
    },
  },
  {
    id: 'sidebar',
    name: 'Sidebar',
    icon: <RectangleVertical className='w-4 h-4' />,
    category: 'Layout',
    defaultComponent: {
      type: 'sidebar',
      x: 0,
      y: 0,
      width: 200,
      height: 400,
      properties: {
        backgroundColor: '#f9fafb',
        borderWidth: 1,
        borderColor: '#e5e7eb',
        padding: 16,
        text: 'Sidebar',
      },
    },
  },
  {
    id: 'footer',
    name: 'Footer',
    icon: <RectangleHorizontal className='w-4 h-4' />,
    category: 'Layout',
    defaultComponent: {
      type: 'footer',
      x: 0,
      y: 0,
      width: 800,
      height: 80,
      properties: {
        backgroundColor: '#374151',
        color: '#ffffff',
        padding: 16,
        text: 'Footer',
      },
    },
  },

  // Navigation Components
  {
    id: 'navbar',
    name: 'Navbar',
    icon: <Menu className='w-4 h-4' />,
    category: 'Navigation',
    defaultComponent: {
      type: 'navbar',
      x: 0,
      y: 0,
      width: 800,
      height: 50,
      properties: {
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#e5e7eb',
        padding: 12,
        text: 'Navigation',
      },
    },
  },
  {
    id: 'breadcrumb',
    name: 'Breadcrumb',
    icon: <Layout className='w-4 h-4' />,
    category: 'Navigation',
    defaultComponent: {
      type: 'breadcrumb',
      x: 0,
      y: 0,
      width: 300,
      height: 30,
      properties: {
        color: '#6b7280',
        fontSize: 14,
        text: 'Home > Page > Current',
      },
    },
  },

  // Form Components
  {
    id: 'input',
    name: 'Input',
    icon: <Type className='w-4 h-4' />,
    category: 'Forms',
    defaultComponent: {
      type: 'input',
      x: 0,
      y: 0,
      width: 200,
      height: 40,
      properties: {
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 4,
        padding: 8,
        text: 'Enter text...',
      },
    },
  },
  {
    id: 'button',
    name: 'Button',
    icon: <Square className='w-4 h-4' />,
    category: 'Forms',
    defaultComponent: {
      type: 'button',
      x: 0,
      y: 0,
      width: 100,
      height: 40,
      properties: {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        borderRadius: 4,
        text: 'Button',
        fontSize: 14,
        fontWeight: '500',
      },
    },
  },
  {
    id: 'textarea',
    name: 'Textarea',
    icon: <Type className='w-4 h-4' />,
    category: 'Forms',
    defaultComponent: {
      type: 'textarea',
      x: 0,
      y: 0,
      width: 300,
      height: 100,
      properties: {
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 4,
        padding: 8,
        text: 'Enter your message...',
      },
    },
  },

  // Data Components
  {
    id: 'card',
    name: 'Card',
    icon: <Square className='w-4 h-4' />,
    category: 'Data',
    defaultComponent: {
      type: 'card',
      x: 0,
      y: 0,
      width: 250,
      height: 200,
      properties: {
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#e5e7eb',
        borderRadius: 8,
        padding: 16,
        text: 'Card Content',
      },
    },
  },
  {
    id: 'table',
    name: 'Table',
    icon: <Grid3X3 className='w-4 h-4' />,
    category: 'Data',
    defaultComponent: {
      type: 'table',
      x: 0,
      y: 0,
      width: 400,
      height: 200,
      properties: {
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#e5e7eb',
        text: 'Table Data',
      },
    },
  },

  // Media Components
  {
    id: 'image',
    name: 'Image',
    icon: <Image className='w-4 h-4' />,
    category: 'Media',
    defaultComponent: {
      type: 'image',
      x: 0,
      y: 0,
      width: 200,
      height: 150,
      properties: {
        backgroundColor: '#f3f4f6',
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 4,
        text: 'Image Placeholder',
      },
    },
  },

  // Icons
  {
    id: 'search-icon',
    name: 'Search',
    icon: <Search className='w-4 h-4' />,
    category: 'Icons',
    defaultComponent: {
      type: 'icon',
      x: 0,
      y: 0,
      width: 24,
      height: 24,
      properties: {
        color: '#6b7280',
        text: '🔍',
      },
    },
  },
  {
    id: 'user-icon',
    name: 'User',
    icon: <User className='w-4 h-4' />,
    category: 'Icons',
    defaultComponent: {
      type: 'icon',
      x: 0,
      y: 0,
      width: 24,
      height: 24,
      properties: {
        color: '#6b7280',
        text: '👤',
      },
    },
  },
  {
    id: 'cart-icon',
    name: 'Shopping Cart',
    icon: <ShoppingCart className='w-4 h-4' />,
    category: 'Icons',
    defaultComponent: {
      type: 'icon',
      x: 0,
      y: 0,
      width: 24,
      height: 24,
      properties: {
        color: '#6b7280',
        text: '🛒',
      },
    },
  },
];

export function WireframeEditor({
  projectId,
  userId,
  wireframeId,
  onSave,
  onClose,
}: WireframeEditorProps) {
  const [components, setComponents] = useState<WireframeComponent[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(
    null
  );
  const [draggedComponent, setDraggedComponent] =
    useState<ComponentTemplate | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [viewport, setViewport] = useState<'desktop' | 'tablet' | 'mobile'>(
    'desktop'
  );
  const [zoom, setZoom] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [wireframeName, setWireframeName] = useState('Untitled Wireframe');
  const [wireframeDescription, setWireframeDescription] = useState('');

  const canvasRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const viewportSizes = {
    desktop: { width: 1200, height: 800 },
    tablet: { width: 768, height: 1024 },
    mobile: { width: 375, height: 667 },
  };

  const loadWireframe = useCallback(async (id: string) => {
    try {
      const wireframes = await RequirementsService.getWireframes(projectId);
      const wireframe = wireframes.find(wireframe => wireframe.id === id);
      if (wireframe) {
        setWireframeName(wireframe.name);
        setWireframeDescription(wireframe.description || '');
        setComponents(wireframe.canvas_data.components || []);
        setCanvasSize(
          wireframe.canvas_data.canvasSize || { width: 800, height: 600 }
        );
      }
    } catch (error) {
      logger.error('Error loading wireframe', { error, wireframeId: id });
    }
  }, [projectId]);

  useEffect(() => {
    if (wireframeId) {
      loadWireframe(wireframeId);
    }
  }, [wireframeId, loadWireframe]);

  const handleDragStart = (template: ComponentTemplate) => {
    setDraggedComponent(template);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (!draggedComponent || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - canvasRect.left) / zoom;
    const y = (e.clientY - canvasRect.top) / zoom;

    const newComponent: WireframeComponent = {
      id: `component-${Date.now()}`,
      ...draggedComponent.defaultComponent,
      x: Math.max(0, x - draggedComponent.defaultComponent.width / 2),
      y: Math.max(0, y - draggedComponent.defaultComponent.height / 2),
    };

    setComponents(prev => [...prev, newComponent]);
    setSelectedComponent(newComponent.id);
    setDraggedComponent(null);
  };

  const handleComponentClick = (componentId: string) => {
    setSelectedComponent(componentId);
  };

  const handleComponentDrag = (componentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragging(true);
    setSelectedComponent(componentId);

    const component = components.find(c => c.id === componentId);
    if (component) {
      setDragOffset({
        x: e.clientX - component.x * zoom,
        y: e.clientY - component.y * zoom,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !selectedComponent) return;

    const newX = (e.clientX - dragOffset.x) / zoom;
    const newY = (e.clientY - dragOffset.y) / zoom;

    setComponents(prev =>
      prev.map(comp =>
        comp.id === selectedComponent
          ? { ...comp, x: Math.max(0, newX), y: Math.max(0, newY) }
          : comp
      )
    );
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updateComponentProperty = (
    componentId: string,
    property: string,
    value: any
  ) => {
    setComponents(prev =>
      prev.map(comp =>
        comp.id === componentId
          ? { ...comp, properties: { ...comp.properties, [property]: value } }
          : comp
      )
    );
  };

  const deleteComponent = (componentId: string) => {
    setComponents(prev => prev.filter(comp => comp.id !== componentId));
    if (selectedComponent === componentId) {
      setSelectedComponent(null);
    }
  };

  const duplicateComponent = (componentId: string) => {
    const component = components.find(c => c.id === componentId);
    if (component) {
      const newComponent: WireframeComponent = {
        ...component,
        id: `component-${Date.now()}`,
        x: component.x + 20,
        y: component.y + 20,
      };
      setComponents(prev => [...prev, newComponent]);
    }
  };

  const generateFromPrompt = async () => {
    if (!aiPrompt.trim()) return;

    setIsGenerating(true);
    try {
      const response = await openaiService.generateResponse({
        prompt: `Generate a wireframe layout based on this description: "${aiPrompt}". 
        
        Return a JSON structure with components array. Each component should have:
        - type: component type (header, sidebar, content, button, etc.)
        - x, y: position
        - width, height: dimensions
        - properties: styling properties like backgroundColor, color, text, etc.
        
        Canvas size: ${canvasSize.width}x${canvasSize.height}
        Viewport: ${viewport}
        
        Make it a realistic wireframe layout.`,
        userId,
        maxTokens: 2000,
        temperature: 0.7,
      });

      // Parse AI response and create components
      try {
        const aiComponents = JSON.parse(response);
        if (Array.isArray(aiComponents)) {
          const newComponents: WireframeComponent[] = aiComponents.map(
            (comp: any, index: number) => ({
              id: `ai-component-${Date.now()}-${index}`,
              type: comp.type || 'container',
              x: comp.x || 0,
              y: comp.y || 0,
              width: comp.width || 100,
              height: comp.height || 50,
              properties: {
                text: comp.properties?.text || '',
                backgroundColor: comp.properties?.backgroundColor || '#f3f4f6',
                color: comp.properties?.color || '#000000',
                ...comp.properties,
              },
            })
          );

          setComponents(prev => [...prev, ...newComponents]);
          setAiPrompt('');
          setShowAIPanel(false);
        }
      } catch (parseError) {
        logger.error('Error parsing AI response', { parseError, response });
        // Fallback: create a simple layout
        const fallbackComponents: WireframeComponent[] = [
          {
            id: `ai-fallback-${Date.now()}`,
            type: 'header',
            x: 0,
            y: 0,
            width: canvasSize.width,
            height: 60,
            properties: {
              backgroundColor: '#1f2937',
              color: '#ffffff',
              text: 'Generated Header',
              padding: 16,
            },
          },
          {
            id: `ai-fallback-${Date.now() + 1}`,
            type: 'container',
            x: 0,
            y: 60,
            width: canvasSize.width,
            height: canvasSize.height - 60,
            properties: {
              backgroundColor: '#f9fafb',
              text: 'Generated Content Area',
              padding: 20,
            },
          },
        ];
        setComponents(prev => [...prev, ...fallbackComponents]);
      }
    } catch (error) {
      logger.error('Error generating wireframe from prompt', {
        error,
        prompt: aiPrompt,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const saveWireframe = async () => {
    try {
      const wireframeData: Wireframe = {
        id: wireframeId || '',
        project_id: projectId,
        name: wireframeName,
        description: wireframeDescription,
        page_type: 'custom',
        canvas_data: {
          components,
          canvasSize,
          viewport,
          zoom,
        },
        created_via_prompt: aiPrompt || undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (wireframeId) {
        // Update existing wireframe
        await RequirementsService.updateWireframe(wireframeId, {
          name: wireframeName,
          description: wireframeDescription,
          canvas_data: wireframeData.canvas_data,
        });
      } else {
        // Create new wireframe
        const newWireframe = await RequirementsService.createWireframe(
          projectId,
          {
            name: wireframeName,
            description: wireframeDescription,
            page_type: 'custom',
            canvas_data: wireframeData.canvas_data,
            created_via_prompt: aiPrompt || undefined,
          }
        );
        wireframeData.id = newWireframe.id;
      }

      onSave?.(wireframeData);
      logger.info('Wireframe saved', { wireframeId: wireframeData.id });
    } catch (error) {
      logger.error('Error saving wireframe', { error, projectId });
    }
  };

  const exportAsImage = () => {
    // This would implement canvas to image export
    // For now, just log the action
    logger.info('Export wireframe as image', {
      componentCount: components.length,
    });
  };

  const selectedComponentData = components.find(
    c => c.id === selectedComponent
  );

  return (
    <div className='h-screen flex flex-col bg-gray-50 dark:bg-gray-900'>
      {/* Header */}
      <div className='bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            <input
              type='text'
              value={wireframeName}
              onChange={e => setWireframeName(e.target.value)}
              className='text-xl font-semibold bg-transparent border-none outline-none text-gray-900 dark:text-white'
            />
            <button
              onClick={() => setShowAIPanel(!showAIPanel)}
              className='badge badge-primary text-sm hover:opacity-80'
            >
              🤖 AI Generate
            </button>
          </div>
          <div className='flex items-center gap-2'>
            <button
              onClick={saveWireframe}
              className='btn btn-primary flex items-center gap-2'
            >
              <Save className='w-4 h-4' />
              Save
            </button>
            <button
              onClick={exportAsImage}
              className='px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2'
            >
              <Download className='w-4 h-4' />
              Export
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className='px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500'
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>

      <div className='flex-1 flex'>
        {/* Component Library */}
        <div className='w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto'>
          <h3 className='font-semibold text-gray-900 dark:text-white mb-4'>
            Components
          </h3>

          {Object.entries(
            COMPONENT_TEMPLATES.reduce(
              (acc, template) => {
                if (!acc[template.category]) acc[template.category] = [];
                acc[template.category].push(template);
                return acc;
              },
              {} as Record<string, ComponentTemplate[]>
            )
          ).map(([category, templates]) => (
            <div key={category} className='mb-4'>
              <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                {category}
              </h4>
              <div className='space-y-1'>
                {templates.map(template => (
                  <div
                    key={template.id}
                    draggable
                    onDragStart={() => handleDragStart(template)}
                    className='flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-grab active:cursor-grabbing'
                  >
                    {template.icon}
                    <span className='text-sm text-gray-700 dark:text-gray-300'>
                      {template.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Main Canvas Area */}
        <div className='flex-1 flex flex-col'>
          {/* Toolbar */}
          <div className='bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-4'>
                {/* Viewport Selector */}
                <div className='flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1'>
                  {(['desktop', 'tablet', 'mobile'] as const).map(vp => (
                    <button
                      key={vp}
                      onClick={() => {
                        setViewport(vp);
                        setCanvasSize(viewportSizes[vp]);
                      }}
                      className={`p-2 rounded ${
                        viewport === vp
                          ? 'bg-surface text-accent-500'
                          : 'text-secondary hover:text-primary'
                      }`}
                    >
                      {vp === 'desktop' && <Monitor className='w-4 h-4' />}
                      {vp === 'tablet' && <Tablet className='w-4 h-4' />}
                      {vp === 'mobile' && <Smartphone className='w-4 h-4' />}
                    </button>
                  ))}
                </div>

                {/* Zoom Controls */}
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
                    className='p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
                  >
                    <Minus className='w-4 h-4' />
                  </button>
                  <span className='text-sm text-gray-600 dark:text-gray-400 w-12 text-center'>
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={() => setZoom(Math.min(2, zoom + 0.25))}
                    className='p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
                  >
                    <Plus className='w-4 h-4' />
                  </button>
                </div>
              </div>

              <div className='flex items-center gap-2'>
                <span className='text-sm text-gray-600 dark:text-gray-400'>
                  {canvasSize.width} × {canvasSize.height}
                </span>
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className='flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 p-8'>
            <div className='inline-block'>
              <div
                ref={canvasRef}
                className='bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 relative'
                style={{
                  width: canvasSize.width * zoom,
                  height: canvasSize.height * zoom,
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top left',
                }}
                onDrop={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
              >
                {components.map(component => (
                  <div
                    key={component.id}
                    className={`absolute cursor-move ${
                      selectedComponent === component.id
                        ? 'ring-2 ring-blue-500'
                        : ''
                    }`}
                    style={{
                      left: component.x,
                      top: component.y,
                      width: component.width,
                      height: component.height,
                      backgroundColor: component.properties.backgroundColor,
                      color: component.properties.color,
                      fontSize: component.properties.fontSize,
                      fontWeight: component.properties.fontWeight,
                      borderRadius: component.properties.borderRadius,
                      borderWidth: component.properties.borderWidth,
                      borderColor: component.properties.borderColor,
                      padding: component.properties.padding,
                      margin: component.properties.margin,
                      zIndex: component.properties.zIndex || 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                    onClick={() => handleComponentClick(component.id)}
                    onMouseDown={e => handleComponentDrag(component.id, e)}
                  >
                    {component.properties.text || component.type}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Properties Panel */}
        {selectedComponentData && (
          <div className='w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-4'>
            <h3 className='font-semibold text-gray-900 dark:text-white mb-4'>
              Properties
            </h3>

            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Text
                </label>
                <input
                  type='text'
                  value={selectedComponentData.properties.text || ''}
                  onChange={e =>
                    updateComponentProperty(
                      selectedComponent!,
                      'text',
                      e.target.value
                    )
                  }
                  className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Background Color
                </label>
                <input
                  type='color'
                  value={
                    selectedComponentData.properties.backgroundColor ||
                    '#ffffff'
                  }
                  onChange={e =>
                    updateComponentProperty(
                      selectedComponent!,
                      'backgroundColor',
                      e.target.value
                    )
                  }
                  className='w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Text Color
                </label>
                <input
                  type='color'
                  value={selectedComponentData.properties.color || '#000000'}
                  onChange={e =>
                    updateComponentProperty(
                      selectedComponent!,
                      'color',
                      e.target.value
                    )
                  }
                  className='w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg'
                />
              </div>

              <div className='grid grid-cols-2 gap-2'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    Width
                  </label>
                  <input
                    type='number'
                    value={selectedComponentData.width}
                    onChange={e => {
                      const newWidth = parseInt(e.target.value);
                      setComponents(prev =>
                        prev.map(comp =>
                          comp.id === selectedComponent!
                            ? { ...comp, width: Math.max(10, newWidth) }
                            : comp
                        )
                      );
                    }}
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    Height
                  </label>
                  <input
                    type='number'
                    value={selectedComponentData.height}
                    onChange={e => {
                      const newHeight = parseInt(e.target.value);
                      setComponents(prev =>
                        prev.map(comp =>
                          comp.id === selectedComponent!
                            ? { ...comp, height: Math.max(10, newHeight) }
                            : comp
                        )
                      );
                    }}
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white'
                  />
                </div>
              </div>

              <div className='flex gap-2 pt-4'>
                <button
                  onClick={() => duplicateComponent(selectedComponent!)}
                  className='flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center gap-2'
                >
                  <Copy className='w-4 h-4' />
                  Duplicate
                </button>
                <button
                  onClick={() => deleteComponent(selectedComponent!)}
                  className='btn-destructive-outline flex-1 inline-flex items-center justify-center gap-2'
                >
                  <Trash2 className='w-4 h-4' />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AI Generation Panel */}
      {showAIPanel && (
        <div className='absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white dark:bg-gray-800 rounded-lg p-6 w-96'>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
              Generate Wireframe with AI
            </h3>
            <textarea
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              placeholder="Describe the wireframe you want to create... (e.g., 'Create a dashboard with sidebar navigation, header, and main content area with cards')"
              className='w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none'
            />
            <div className='flex gap-2 mt-4'>
              <button
                onClick={generateFromPrompt}
                disabled={!aiPrompt.trim() || isGenerating}
                className='btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
              >
                {isGenerating ? (
                  <>
                    <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className='w-4 h-4' />
                    Generate
                  </>
                )}
              </button>
              <button
                onClick={() => setShowAIPanel(false)}
                className='px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500'
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
